<template>
  <el-card class="page-card" shadow="never" v-loading="fetching || exporting">
    <template #header>
      <div class="card-header">
        <el-form
          inline
          size="small"
          v-if="roleKind !== 'salesman' && roleKind !== 'customer_service'"
        >
          <el-form-item label="业务员">
            <el-select
              v-model="userId"
              placeholder="请选择"
              filterable
              clearable
            >
              <el-option
                v-for="user in users"
                :key="user.id"
                :label="user.screen_name"
                :value="user.id"
              />
            </el-select>
          </el-form-item>
        </el-form>

        <div class="btn-group">
          <el-button
            v-if="canSyncData"
            type="primary"
            @click="syncData"
            size="small"
            >同步聚水潭数据</el-button
          >
          <el-button
            class="export-btn"
            type="primary"
            @click="exportExcel"
            size="small"
            :loading="exporting"
            >导出</el-button
          >
          <el-button
            class="export-btn"
            type="success"
            @click="exportDailyImage"
            size="small"
            :loading="exporting"
            >导出日报图片</el-button
          >
          <el-button
            class="export-btn"
            type="success"
            @click="exportDailyImageToClipboard"
            size="small"
            :loading="exporting"
            >复制日报图片到剪切板</el-button
          >
          <template v-if="showGroupSummary">
            <el-button
              class="export-btn"
              type="danger"
              @click="exportTeamDailyImage"
              size="small"
              :loading="exporting"
              >导出管理日报图片</el-button
            >
            <el-button
              class="export-btn"
              type="danger"
              @click="exportTeamDailyImageToClipboard"
              size="small"
              :loading="exporting"
              >复制管理日报图片到剪切板</el-button
            >
          </template>
        </div>
      </div>
    </template>

    <div class="field-descriptions">
      <el-tooltip effect="dark" placement="right">
        <template #content>
          <div class="tooltip-content">
            <p>跟进客户数量-新：当日跟进客户里面新客户的数量</p>
            <p>跟进客户数量-旧：当日跟进客户里面旧客户的数量</p>
            <p>
              样板-新增：读取飞鸽那边的数据，是当日新增的数量，匹配是通过跟飞鸽的业务员名称匹配
            </p>
            <p>订单-下单数量：当日新增的销售订单数量</p>
            <p>订单-下单金额：当日新增的销售订单金额</p>
            <p>出货-出货数量：已发货聚水潭订单当日的数量</p>
            <p>出货-出货金额：已发货聚水潭订单当日的金额</p>
            <p>财务-回款：当日审核通过的收款金额</p>
            <p>财务-应收未收：当日新增的应收未收金额</p>
            <p>财务-应收逾期：当日之前两个月仍未完结的应收金额</p>
            <p>批发总额：聚水潭的全部金额 - 销售订单是定制类型的金额</p>
            <p>关键事项：当日跟进记录中关联的各关键事项次数</p>
          </div>
        </template>
        <el-button type="text" class="help-icon">
          表格说明
          <i class="el-icon-question"></i>
        </el-button>
      </el-tooltip>
    </div>

    <!-- 日报表格 -->
    <Table
      v-if="showDailyTable"
      ref="tableRef"
      :data="dailyData"
      :userId="dailyTableMode === 'common' ? currentUser.id : ''"
      :users="users"
      :roleKind="roleKind"
      :class="{
        'daily-table': dailyTableMode === 'common',
        'team-daily-table': dailyTableMode === 'group',
      }"
      :start-on="dailyStartOn"
      show-full-text-content
      :show-group-summary="showGroupSummary && dailyTableMode === 'group'"
      is-report-mode
      :show-finish-target="showFinishTarget"
      :show-wholesale-amount="showWholesaleAmount"
      :follow-key-item-names="dailyFollowKeyItemNames"
      :report-metrics="dailyReportMetrics"
    />

    <div
      ref="tableContainer"
      class="table-container"
      @scroll.passive="handleTableScroll"
    >
      <Table
        :show-finish-target="showFinishTarget"
        :show-wholesale-amount="showWholesaleAmount"
        :users="users"
        :data="data"
        :userId="userId"
        :roleKind="roleKind"
        class="common-table"
        :start-on="startOn"
        :show-group-summary="showGroupSummary"
        :follow-key-item-names="followKeyItemNames"
        :user-render-limit="renderedUserCount"
        :report-metrics="reportMetrics"
      />
    </div>
    <div v-if="hasMoreUsers" class="render-progress">
      已渲染 {{ renderedUserCount }} / {{ filteredDataCount }} 位业务员，向下滚动继续加载
    </div>
  </el-card>
</template>

<script>
import { Vue, Component, Prop, Watch, Mixins } from 'vue-property-decorator';
import Utils from '@/components/utils.vue';
import Table from './table.vue';
import * as XLSX from 'xlsx';
import { sleep, abilityService } from 'beans-admin-plugin';
import moment from 'moment';
import { toPng, toBlob } from 'html-to-image';
import _ from 'lodash';

const USER_BATCH_SIZE = 8;
const SUMMARY_KEYS = Object.freeze([
  'followed_new_customers_count',
  'followed_customers_count',
  'new_product_designs_count',
  'new_sales_orders_quantity',
  'new_orders_quantity',
  'new_sales_orders_amount',
  'new_orders_amount',
  'payments_amount',
  'pending_payments_amount',
  'overdue_payments_amount',
  'new_feige_orders_count',
]);
const REPORT_DEFAULT_KEYS = Object.freeze([
  'followed_customers_count',
  'followed_new_customers_count',
  'new_product_designs_count',
  'new_feige_orders_count',
  'new_sales_orders_quantity',
  'new_sales_orders_amount',
  'new_orders_quantity',
  'new_orders_amount',
  'new_after_sales_quantity',
  'new_after_sales_amount',
  'payments_amount',
  'pending_payments_amount',
  'overdue_payments_amount',
  'work_report_content',
  'work_report_plan',
  'work_report_inquiry_content',
  'performance_goal',
  'follow_key_items_count',
]);
const DEFAULT_GROUP_PERFORMANCE_GOAL = Object.freeze({
  finish_target: 0,
  finish_amount: 0,
  finish_percent: '-',
  finish_diff: 0,
  sales_target: 0,
  sales_amount: 0,
  sales_percent: '-',
  sales_diff: 0,
  shipping_target: 0,
  shipping_amount: 0,
  shipping_percent: '-',
  shipping_diff: 0,
  wholesale_amount: 0,
});
const EMPTY_REPORT_METRICS = Object.freeze({
  dates: Object.freeze([]),
  summary: Object.freeze([]),
  summaryKeyByUserId: Object.freeze({}),
  groupData: Object.freeze({}),
  groupPerformanceGoal: DEFAULT_GROUP_PERFORMANCE_GOAL,
});

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) {
    return value;
  }
  Object.keys(value).forEach(key => {
    deepFreeze(value[key]);
  });
  return Object.freeze(value);
}

function getReportDates(startOn) {
  const startDate = moment(startOn).startOf('month');
  const today = moment();
  const daysInMonth = startDate.daysInMonth();
  const dates = [];

  for (let i = 0; i < daysInMonth; i += 1) {
    const currentDate = moment(startDate).add(i, 'days');
    if (currentDate.isAfter(today)) {
      break;
    }
    dates.push(currentDate.format('YYYY-MM-DD'));
  }

  return dates.reverse();
}

function sumObjectValues(record) {
  if (!record) return 0;
  return Object.values(record).reduce((total, value) => total + (Number(value) || 0), 0);
}

function getLatestMetricValue(record, dates = []) {
  if (!record || typeof record !== 'object') return 0;
  if (dates.length) {
    const matchedDate = dates.find(date => Object.prototype.hasOwnProperty.call(record, date));
    if (matchedDate) {
      return Number(record[matchedDate]) || 0;
    }
  }
  const latestKey = Object.keys(record).sort().pop();
  return latestKey ? (Number(record[latestKey]) || 0) : 0;
}

function buildSummary(data, dates, followKeyItemNames) {
  return data.map(item => {
    const summaryItem = {
      user_id: item.user_id,
    };

    SUMMARY_KEYS.forEach(key => {
      if (key === 'pending_payments_amount' || key === 'overdue_payments_amount') {
        summaryItem[key] = getLatestMetricValue(item[key], dates);
        return;
      }
      summaryItem[key] = sumObjectValues(item[key]);
    });

    const followKeyItems = {};
    followKeyItemNames.forEach(name => {
      followKeyItems[name] = sumObjectValues(_.get(item, `follow_key_items_count.${name}`, {}));
    });
    summaryItem.follow_key_items = followKeyItems;

    const finishTarget = Number(_.get(item, 'performance_goal.finish_target')) || 0;
    const finishAmount = Number(_.get(item, 'performance_goal.finish_amount')) || 0;
    const salesTarget = Number(_.get(item, 'performance_goal.sales_target')) || 0;
    const salesAmount = Number(summaryItem.new_sales_orders_amount) || 0;
    const shippingTarget = Number(_.get(item, 'performance_goal.shipping_target')) || 0;
    const shippingAmount = Number(_.get(item, 'performance_goal.shipping_amount')) || 0;
    const wholesaleAmount = Number(_.get(item, 'performance_goal.wholesale_amount')) || 0;

    summaryItem.performance_goal = {
      finish_target: finishTarget,
      finish_amount: finishAmount,
      finish_percent: finishTarget ? `${(finishAmount / finishTarget * 100).toFixed(0)}%` : '-',
      finish_diff: finishTarget - finishAmount,
      sales_target: salesTarget,
      sales_amount: salesAmount,
      sales_percent: salesTarget ? `${(salesAmount / salesTarget * 100).toFixed(0)}%` : '-',
      sales_diff: salesTarget - salesAmount,
      shipping_target: shippingTarget,
      shipping_amount: shippingAmount,
      shipping_percent: shippingTarget ? `${(shippingAmount / shippingTarget * 100).toFixed(0)}%` : '-',
      shipping_diff: shippingTarget - shippingAmount,
      wholesale_amount: wholesaleAmount,
    };

    return deepFreeze(summaryItem);
  });
}

function buildGroupData(data, dates, followKeyItemNames, summary) {
  const result = {
    followed_new_customers_count: {
      customer_service: {},
      common: {},
    },
    followed_customers_count: {
      customer_service: {},
      common: {},
    },
  };
  const summaryKeyByUserId = _.keyBy(summary, 'user_id');

  data.forEach(item => {
    dates.forEach(date => {
      SUMMARY_KEYS.forEach(key => {
        const val = Number(_.get(item, `${key}.${date}`)) || 0;
        let path = `${key}.${date}`;

        if (key === 'followed_new_customers_count' || key === 'followed_customers_count') {
          const groupKey = item.role_kind === 'customer_service' ? 'customer_service' : 'common';
          path = `${key}.${groupKey}.${date}`;
        }

        _.set(result, path, val + (_.get(result, path) || 0));
      });
    });
  });

  data.forEach(item => {
    const summaryItem = summaryKeyByUserId[item.user_id];
    if (!summaryItem) return;

    SUMMARY_KEYS.forEach(key => {
      if (key === 'followed_new_customers_count' || key === 'followed_customers_count') {
        const groupKey = item.role_kind === 'customer_service' ? 'customer_service' : 'common';
        const totalPath = `${key}.${groupKey}.total`;
        _.set(result, totalPath, (Number(_.get(result, totalPath)) || 0) + (Number(summaryItem[key]) || 0));
        return;
      }

      const totalPath = `${key}.total`;
      _.set(result, totalPath, (Number(_.get(result, totalPath)) || 0) + (Number(summaryItem[key]) || 0));
    });
  });

  result.follow_key_items = {};
  followKeyItemNames.forEach(name => {
    result.follow_key_items[name] = { total: 0 };
    data.forEach(item => {
      dates.forEach(date => {
        const val = Number(_.get(item, `follow_key_items_count.${name}.${date}`)) || 0;
        result.follow_key_items[name][date] = (result.follow_key_items[name][date] || 0) + val;
      });

      const summaryItem = summaryKeyByUserId[item.user_id];
      result.follow_key_items[name].total += Number(_.get(summaryItem, `follow_key_items.${name}`)) || 0;
    });
  });

  return deepFreeze(result);
}

function buildGroupPerformanceGoal(summary) {
  const result = {
    ...DEFAULT_GROUP_PERFORMANCE_GOAL,
  };

  summary.forEach(item => {
    result.finish_target += Number(_.get(item, 'performance_goal.finish_target')) || 0;
    result.sales_target += Number(_.get(item, 'performance_goal.sales_target')) || 0;
    result.shipping_target += Number(_.get(item, 'performance_goal.shipping_target')) || 0;
    result.finish_amount += Number(_.get(item, 'performance_goal.finish_amount')) || 0;
    result.sales_amount += Number(_.get(item, 'performance_goal.sales_amount')) || 0;
    result.shipping_amount += Number(_.get(item, 'performance_goal.shipping_amount')) || 0;
    result.wholesale_amount += Number(_.get(item, 'performance_goal.wholesale_amount')) || 0;
  });

  result.sales_percent = result.sales_target ? `${(result.sales_amount / result.sales_target * 100).toFixed(0)}%` : '-';
  result.finish_percent = result.finish_target ? `${(result.finish_amount / result.finish_target * 100).toFixed(0)}%` : '-';
  result.shipping_percent = result.shipping_target ? `${(result.shipping_amount / result.shipping_target * 100).toFixed(0)}%` : '-';
  result.sales_diff = result.sales_target - result.sales_amount;
  result.finish_diff = result.finish_target - result.finish_amount;
  result.shipping_diff = result.shipping_target - result.shipping_amount;

  return deepFreeze(result);
}

function buildReportMetrics(data, startOn, followKeyItemNames) {
  if (!data.length) {
    return EMPTY_REPORT_METRICS;
  }

  const dates = getReportDates(startOn);
  const summary = deepFreeze(buildSummary(data, dates, followKeyItemNames));
  const summaryKeyByUserId = deepFreeze(_.keyBy(summary, 'user_id'));

  return deepFreeze({
    dates: deepFreeze(dates.slice()),
    summary,
    summaryKeyByUserId,
    groupData: buildGroupData(data, dates, followKeyItemNames, summary),
    groupPerformanceGoal: buildGroupPerformanceGoal(summary),
  });
}

@Component({
  components: {
    Table,
  },
})
export default class TableList extends Mixins(Vue, Utils) {
  @Prop({ type: String }) startOn; // 2024-11
  @Prop({ type: Array }) users;

  dailyStartOn = moment().format('YYYY-MM');
  todayDate = moment().format('YYYY-MM-DD');

  dailyData = [];
  data = [];
  followKeyItemNames = [];
  dailyFollowKeyItemNames = [];
  reportMetrics = EMPTY_REPORT_METRICS;
  dailyReportMetrics = EMPTY_REPORT_METRICS;
  userId = null;
  fetching = false;
  request = null;
  exporting = false;
  renderedUserCount = USER_BATCH_SIZE;

  showDailyTable = false;
  dailyTableMode = 'group'; // common, group

  imageExportOptions = {
    pixelRatio: 3,
    quality: 1,
  };

  get showWholesaleAmount() {
    // TODO: Implement logic to determine if wholesale amount should be shown
    return false;
  }

  get showFinishTarget() {
    return this.performanceGoalTargets.includes('finish_target');
  }

  get showGroupSummary() {
    return this.roleKind !== 'salesman' && this.roleKind !== 'customer_service';
  }

  get canSyncData() {
    return abilityService.can('jushuitan_orders.sync_recent_data');
  }

  get filteredDataCount() {
    if (!this.userId) {
      return this.data.length;
    }
    return this.data.filter(item => String(item.user_id) === String(this.userId)).length;
  }

  get hasMoreUsers() {
    return !this.userId && this.filteredDataCount > this.renderedUserCount;
  }

  async exportExcel() {
    this.exporting = true;
    try {
      const TableConstructor = Vue.extend(Table);
      const tableInstance = new TableConstructor({
        i18n: this.$i18n,
        router: this.$router,
        propsData: {
          data: this.data,
          userId: this.userId,
          exportMode: true,
          roleKind: this.roleKind,
          startOn: this.startOn,
          users: this.users,
          showFinishTarget: this.showFinishTarget,
          showWholesaleAmount: this.showWholesaleAmount,
          showGroupSummary: this.showGroupSummary,
          followKeyItemNames: this.followKeyItemNames,
          reportMetrics: this.reportMetrics,
        },
      }).$mount();
      await sleep(1000);
      const book = XLSX.utils.table_to_book(tableInstance.$el, {
        // 不加这个会自动识别日期，数字，百分比等，会把本来没有时分秒的日期加上时分秒。百分比会被改成小数...
        raw: true, // 不自动处理数字，日期等
      });
      const ws = book.Sheets[book.SheetNames[0]];
      ws['!cols'] = Array(40).fill({ wch: 13 });
      XLSX.writeFile(
        book,
        `${this.$t('model.reports/sales_follows')}-${this.startOn}.xlsx`
      );
      tableInstance.$destroy();
      tableInstance.$el.remove();
    } finally {
      this.exporting = false;
    }
  }

  created() {
    // 使用防抖包装导出函数
    this.debouncedExportImage = _.debounce(
      async (type, isClipboard = false) => {
        if (this.exporting) return;

        this.exporting = true;
        const isTeam = type === 'group';
        let blob = null;
        try {
          this.showDailyTable = true;
          this.dailyTableMode = type;
          await this.$nextTick();

          const element = this.$refs.tableRef.$el;

          if (isClipboard) {
            blob = await toBlob(element, this.imageExportOptions);
            await navigator.clipboard.write([
              new window.ClipboardItem({ [blob.type]: blob }),
            ]);
            this.$message.success(
              `已复制${isTeam ? '管理' : ''}日报图片到剪切板`
            );
          } else {
            const png = await toPng(element, this.imageExportOptions);
            const a = document.createElement('a');
            a.href = png;
            a.download = `${isTeam ? '管理' : ''}日报-${this.todayDate}.png`;
            a.click();
            a.remove(); // 清理DOM
          }
        } finally {
          this.showDailyTable = false;
          this.exporting = false;
          if (blob) {
            URL.revokeObjectURL(blob);
          }
        }
      },
      300
    );
  }

  async exportDailyImage() {
    await this.debouncedExportImage('common');
  }

  async exportDailyImageToClipboard() {
    await this.debouncedExportImage('common', true);
  }

  async exportTeamDailyImage() {
    await this.debouncedExportImage('group');
  }

  async exportTeamDailyImageToClipboard() {
    await this.debouncedExportImage('group', true);
  }

  beforeDestroy() {
    if (this.request && this.request.engine) {
      this.request.engine.abort();
    }
  }

  resetRenderedUsers() {
    this.renderedUserCount = USER_BATCH_SIZE;
  }

  loadMoreUsers() {
    if (!this.hasMoreUsers) return;
    this.renderedUserCount += USER_BATCH_SIZE;
  }

  handleTableScroll() {
    const container = this.$refs.tableContainer;
    if (!container || !this.hasMoreUsers) return;
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 400) {
      this.loadMoreUsers();
    }
  }

  async ensureRenderedContentFillsContainer() {
    await this.$nextTick();
    const container = this.$refs.tableContainer;
    let attempts = 0;
    while (container && this.hasMoreUsers && container.scrollHeight <= container.clientHeight && attempts < 20) {
      this.loadMoreUsers();
      attempts += 1;
      await this.$nextTick();
    }
  }

  @Watch('userId')
  async onUserIdChange() {
    this.resetRenderedUsers();
    await this.ensureRenderedContentFillsContainer();
  }

  @Watch('startOn', { immediate: true })
  async fetchPerformancesRates() {
    if (!this.startOn) return;
    this.fetching = true;
    try {
      if (this.request && this.request.engine) {
        this.request.engine.abort();
      }
      this.request = this.$request.get(
        '/reports/sales_follows',
        {
          start_on: `${this.startOn}-01`,
        },
        { timeout: 3 * 60 * 1000, loading: false }
      );
      const data = await this.request;
      // 这是只读报表数据，冻结后可避免 Vue 递归建立深层响应式，显著降低大表查询时的内存占用。
      const processedData = Object.freeze(data.map(item => {
        const processedItem = { ...item };
        REPORT_DEFAULT_KEYS.forEach(key => {
          if (!processedItem[key]) {
            processedItem[key] = {};
          }
        });
        return Object.freeze(processedItem);
      }));
      const followKeyItemNames = Object.freeze(_.get(processedData, '[0].follow_key_item_names', []).slice());
      const reportMetrics = buildReportMetrics(processedData, this.startOn, followKeyItemNames);
      this.resetRenderedUsers();
      if (this.startOn === this.dailyStartOn) {
        this.dailyData = processedData;
        this.dailyReportMetrics = reportMetrics;
        this.dailyFollowKeyItemNames = followKeyItemNames;
      }
      this.data = processedData;
      this.followKeyItemNames = followKeyItemNames;
      this.reportMetrics = reportMetrics;
      await this.ensureRenderedContentFillsContainer();
    } finally {
      this.fetching = false;
    }
  }

  async syncData() {
    await this.$request.post('/jushuitan_orders/sync_recent_data');
    this.$message.success('操作成功，请等待几分钟后查看');
  }
}
</script>

<style scoped lang="scss">
.page-card {
  flex: 1;
  display: flex;
  flex-direction: column;

  /deep/.el-card__header {
    flex-shrink: 0;
    padding: 10px;
  }

  /deep/.el-card__body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 10px;
  }
}

.table-container {
  position: relative;
  width: 100%;
  flex: 1;
  overflow: auto;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;

  .el-form-item {
    margin-bottom: 0;
  }

  .btn-group {
    margin-left: auto;
  }
}

.common-table {
  --work-content-width: 120px;

  /deep/ .column-work-content {
    width: var(--work-content-width);
    min-width: var(--work-content-width);
    max-width: var(--work-content-width);
  }
}

.common-table {
  width: max-content;
}

.render-progress {
  margin-top: 8px;
  color: #606266;
  font-size: 12px;
}

.daily-table,
.team-daily-table {
  --daily-table-width: 200px;
  --work-content-width: 120px;
  width: calc(var(--daily-table-width) + 100px * 3 + var(--work-content-width));

  /deep/ .column-work-content {
    width: var(--work-content-width);
    min-width: var(--work-content-width);
    max-width: var(--work-content-width);
  }

  /deep/ .column-date {
    width: var(--daily-table-width);
    min-width: unset;
    max-width: unset;
  }
}

.help-icon {
  padding: 0;
  font-size: 12px;

  .el-icon-question {
    margin-right: 4px;
  }
}

.tooltip-content {
  p {
    margin: 8px 0;
    white-space: nowrap;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
